import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import _ from "lodash";

import { ACTION_BY_FILTERS_KEY } from "@/Constants/common";
import { _fetchAuthorisedTimesheetList } from "@/services/timesheet";
import { handleAPICatch } from "@/utilities/utils";

import { formatAuthorisedTimesheets } from "../../utilities/dataFormatter";

const initialState = {
  isLoading: false,
  isPaginationLoading: false,
  authorisedTimesheets: [],
  selectedCandidate: [], // list of candidate id
  selectedSignOffIds: [], // list of signoff ids
  selectedTimesheets: [], // list of timesheet ids
  filters: {
    search: "",
    sessionType: [],
    candidateReferenceNo: "",
    contractReferenceNo: "",
    candidateId: [],
    candidateRole: null,
    complianceStatus: null,
    miles: null,
    candidateLicenseNo: "",
    candidatePostcode: "",
    workedDateFrom: "",
    workedDateTo: "",
    contractDateFrom: "",
    contractDateTo: "",
    ...ACTION_BY_FILTERS_KEY,
  },
  sorting: {
    sortByColumn: "",
    sortByOrder: "",
  },
  totalCount: 0,
  isSelectedAll: false,
  oldFilters: {},
  pagination: {
    currentPage: 1,
    totalPage: 1,
    isLastPage: false,
  },
  // showRejectModal: false,
  // showAdjustAmendmentModal: false,
  // adjustAmendMentData: {},
  showFilters: false,
  toggleSelected: false,
  isInvoiceTimesheetModalOpen: false,
};

export const fetchAuthorisedTimesheets = createAsyncThunk(
  "timesheetsAuthorised/fetchAuthorisedTimesheets",
  async (data, { dispatch, getState }) => {
    const {
      clientTimesheetAuthorised: {
        filters,
        pagination,
        sorting,
        authorisedTimesheets,
      },
    } = getState();

    if (_.isEmpty(data)) {
      data = {
        ...filters,
      };
    }
    if (pagination.isLastPage) {
      return dispatch(setAuthorisedTimesheetsLoading(false));
    }

    dispatch(setPaginationLoading(pagination.currentPage !== 1));
    dispatch(toggleSelectedAll(false));
    const _data = {
      ...data,
      currentPage: pagination.currentPage,
      ...sorting,
      timesheetType: "authorised",
    };
    let response = _fetchAuthorisedTimesheetList(_data);
    response
      .then((res) => {
        console.log(res);
        const {
          data: { timesheet_list, meta },
        } = res;

        const { currentPage, lastPage, isLastPage, total: totalCount } = meta;

        let data = formatAuthorisedTimesheets(timesheet_list);

        dispatch(
          updatePagination({
            currentPage: currentPage + 1,
            totalPage: lastPage,
            isLastPage: isLastPage,
          }),
        );
        let prevList = currentPage === 1 ? [] : _.uniq(authorisedTimesheets);
        let firstTimesheetId = data.length > 0 ? data[0].timesheet_id : null;
        dispatch(updateTotalCount(totalCount));
        if (
          prevList.length > 0 &&
          prevList.find((item) => item.timesheet_id === firstTimesheetId) !==
            undefined
        ) {
          //
        } else {
          dispatch(updateAuthorisedTimesheets([...prevList, ...data]));
        }
      })
      .catch((error) => {
        handleAPICatch(error);
      })
      .finally(() => {
        setTimeout(() => {
          dispatch(setAuthorisedTimesheetsLoading(false));
          dispatch(setPaginationLoading(false));
        }, 10);
      });
  },
);

const timesheetsAuthorisedSlice = createSlice({
  name: "timesheetsAuthorised",
  initialState,
  reducers: {
    updateAuthorisedTimesheets: (state, action) => {
      state.authorisedTimesheets = action.payload;
    },
    setAuthorisedTimesheetsLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setPaginationLoading(state, action) {
      state.isPaginationLoading = action.payload; // New reducer for pagination loading
    },
    updateSelectedCandidate: (state, action) => {
      state.selectedCandidate = action.payload;
      if (action.payload.length === 0) {
        state.isSelectedAll = false;
      }
    },
    updateSelectedSignOffIds: (state, action) => {
      state.selectedSignOffIds = action.payload;
    },
    updateSelectedTimesheets: (state, action) => {
      state.selectedTimesheets = action.payload;
    },
    deSelectAllCandidate: (state) => {
      state.selectedSignOffIds = [];
      state.selectedCandidate = [];
      state.selectedTimesheets = [];
      state.isSelectedAll = false;
      state.toggleSelected = !state.toggleSelected;
    },
    updateFilters: (state, action) => {
      if (_.isEmpty(action.payload)) {
        state.filters = {
          search: state.filters.search,
          ...initialState.filters,
        };
        return;
      } else {
        if (action.payload.reset) {
          state.filters = initialState.filters;
        } else {
          state.filters = { ...state.filters, ...action.payload };
        }
      }
    },
    updateSorting: (state, action) => {
      state.sorting = action.payload;
    },
    updatePagination: (state, action) => {
      state.pagination = {
        ...state.pagination,
        ...action.payload,
      };
    },
    showFilterModal: (state, action) => {
      if (action.payload) {
        state.oldFilters = state.filters;
      }
      state.showFilters = action.payload;
    },
    updateToggleSelected: (state, action) => {
      state.toggleSelected = !state.toggleSelected;
    },
    reStateFilters: (state, action) => {
      state.filters = state.oldFilters;
    },
    updateTotalCount: (state, action) => {
      state.totalCount = action.payload;
    },
    toggleSelectedAll: (state, action) => {
      state.isSelectedAll = action.payload;
    },
    toggleInvoiceTimesheetModal: (state, action) => {
      state.isInvoiceTimesheetModalOpen = action.payload;
    },
  },
});

export const {
  updateAuthorisedTimesheets,
  setAuthorisedTimesheetsLoading,
  setPaginationLoading,
  updateSelectedCandidate,
  updateSelectedSignOffIds,
  updateSelectedTimesheets,
  deSelectAllCandidate,
  updateFilters,
  updateSorting,
  updatePagination,
  showFilterModal,
  updateToggleSelected,
  reStateFilters,
  updateTotalCount,
  toggleSelectedAll,
  toggleInvoiceTimesheetModal,
} = timesheetsAuthorisedSlice.actions;

export default timesheetsAuthorisedSlice.reducer;
